import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LegalLinks from '../../components/LegalLinks';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, ArrowLeft } from 'lucide-react';
import clientApi from '../../lib/clientApi';
import { setClientToken, setClientUser } from '../../lib/clientAuth';
import { validateEmail, getErrorMessage } from '../../lib/auth';
import { getNextPathAfterLogin, clearSessionExpiryFlag } from '../../lib/sessionRedirect';
import { storeSessionTokens } from '../../lib/refreshToken';

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) { toast.error('Please enter a valid email address'); return; }
    setLoading(true);
    try {
      const response = await clientApi.post('/api/client/auth/login', formData);
      setClientToken(response.data.token);
      setClientUser(response.data.user);
      storeSessionTokens('client', response.data);
      clearSessionExpiryFlag();
      toast.success(`Welcome, ${response.data.user.name}!`);
      navigate(getNextPathAfterLogin('/client/dashboard'), { replace: true });
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111]" data-testid="client-login-page">
      <div className={`w-full ${isMobile ? 'px-5' : 'max-w-[420px]'}`}>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/hed-icon.png" alt="HED" className="w-8 h-8 rounded-lg" />
            <div className="w-px h-6 bg-white/20" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold text-white">Girgit</span>
          </div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#3B82F6] text-white">Client Portal</span>
        </div>

        <div className="bg-white/[0.08] backdrop-blur-xl border border-white/10 rounded-2xl p-8" data-testid="client-login-card">
          <h2 className="text-xl font-bold text-white mb-1">Welcome</h2>
          <p className="text-sm text-neutral-400 mb-8">Sign in to your client portal</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30 transition-colors"
                  placeholder="you@company.com" data-testid="client-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input type="password" required value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30 transition-colors"
                  placeholder="Enter your password" data-testid="client-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-sm rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="client-login-submit">
              {loading ? <><Loader2 className="animate-spin w-4 h-4" />Signing in...</> : 'Sign In'}
            </button>
          </form>
        </div>

        <div className="mt-8 text-center space-y-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors" data-testid="client-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <LegalLinks tone="dark" />
          <p className="text-xs text-neutral-600">&copy; {new Date().getFullYear()} Half Eaten Donut</p>
        </div>
      </div>
    </div>
  );
}
